import type {
  ContentBlock,
  Citation,
  Reference,
  SummaryMetadata,
  SummaryResponse,
} from '../types';
import type {
  RawContentBlock,
  RawCitation,
  RawReference,
  RawMetadata,
  RawSummaryResponse,
  RawSubCategorySummary,
} from './types';

// Transform content blocks (both new cite format and legacy format)
export function transformContentBlocks(contentBlocks: RawContentBlock[]): ContentBlock[] {
  if (!contentBlocks) return [];

  return contentBlocks.map((block): ContentBlock => {
    // Handle new "cite" type blocks - convert to camelCase
    if (block.type === 'cite' && block.cite) {
      return {
        type: 'cite' as const,
        cite: {
          type: 'char_location' as const,
          cited_text: block.cite.cited_text ?? block.cite.citedText ?? '',
          document_index: block.cite.document_index ?? block.cite.documentIndex ?? 0,
          start_char_index: block.cite.start_char_index ?? block.cite.startCharIndex ?? 0,
          end_char_index: block.cite.end_char_index ?? block.cite.endCharIndex ?? 0,
        }
      };
    }

    // Handle text blocks with optional citations
    return {
      type: 'text' as const,
      text: block.text ?? '',
      citations: block.citations?.map((citation: RawCitation): Citation => ({
        type: 'char_location',
        documentIndex: citation.document_index ?? citation.documentIndex ?? 0,
        documentTitle: citation.document_title ?? citation.documentTitle,
        citedText: citation.cited_text ?? citation.citedText ?? '',
        startCharIndex: citation.start_char_index ?? citation.startCharIndex ?? 0,
        endCharIndex: citation.end_char_index ?? citation.endCharIndex ?? 0,
        fileId: citation.file_id ?? citation.fileId,
      })) || undefined,
    };
  });
}

// Transform references from snake_case to camelCase
// Note: documentIndex may not be in the data - use array index as fallback
export function transformReferences(refs: RawReference[]): Reference[] {
  if (!refs) return [];
  return refs.map((ref, index): Reference => ({
    documentIndex: ref.document_index ?? ref.documentIndex ?? index,
    title: ref.title,
    url: ref.url,
    sourceName: ref.source_name ?? ref.sourceName ?? '',
    rssDescription: ref.rss_description ?? ref.rssDescription ?? null,
  }));
}

// Transform metadata from response
export function transformMetadata(meta: RawMetadata | undefined): SummaryMetadata | undefined {
  if (!meta) return undefined;
  return {
    modelUsed: meta.modelUsed ?? meta.model_used ?? '',
    articlesProcessed: meta.articlesProcessed ?? meta.articles_processed ?? 0,
    clustersProcessed: meta.clustersProcessed ?? meta.clusters_processed ?? 0,
    averageInterestScore: meta.averageInterestScore ?? meta.average_interest_score ?? 0,
    headlinesEvaluated: meta.headlinesEvaluated ?? meta.headlines_evaluated ?? 0,
  };
}

// Transform summary response with content blocks
export function transformSummaryResponse(response: RawSummaryResponse): SummaryResponse {
  const rootMeta = response.metadata ?? {};
  const result: SummaryResponse = {
    categorySlug: response.categorySlug ?? response.category_slug ?? '',
    categoryName: response.categoryName ?? response.category_name ?? '',
    date: response.date,
    edition: response.edition,
    hasSubCategories: response.hasSubCategories ?? response.has_sub_categories ?? false,
    enableContrasting: response.enableContrasting ?? response.enable_contrasting ?? false,
    generatedAt: response.generatedAt ?? response.generated_at ?? '',
    metadata: {
      modelUsed: rootMeta.modelUsed ?? rootMeta.model_used ?? '',
      articlesProcessed: rootMeta.articlesProcessed ?? rootMeta.articles_processed ?? 0,
      clustersProcessed: rootMeta.clustersProcessed ?? rootMeta.clusters_processed ?? 0,
      averageInterestScore: rootMeta.averageInterestScore ?? rootMeta.average_interest_score ?? 0,
      headlinesEvaluated: rootMeta.headlinesEvaluated ?? rootMeta.headlines_evaluated ?? 0,
    },
    summary: null,
    subCategorySummaries: null,
    contrast: null,
    availableEditions: (response.availableEditions ?? response.available_editions ?? []).map(e => ({
      edition: e.edition,
      generatedAt: e.generatedAt ?? e.generated_at ?? '',
    })),
  };

  // Transform main summary if present
  if (response.summary) {
    result.summary = {
      headline: response.summary.headline,
      contentBlocks: transformContentBlocks(response.summary.contentBlocks ?? response.summary.content_blocks ?? []),
      references: transformReferences(response.summary.references ?? []),
      metadata: transformMetadata(response.summary.metadata),
    };
  }

  // Transform sub-category summaries if present
  const subCats = response.subCategorySummaries ?? response.sub_category_summaries;
  if (subCats && Array.isArray(subCats)) {
    result.subCategorySummaries = subCats.map((sub: RawSubCategorySummary) => ({
      subCategory: {
        slug: sub.subCategory?.slug ?? sub.sub_category?.slug ?? '',
        name: sub.subCategory?.name ?? sub.sub_category?.name ?? '',
        sortOrder: sub.subCategory?.sortOrder ?? sub.sub_category?.sort_order ?? 0,
      },
      headline: sub.headline,
      contentBlocks: transformContentBlocks(sub.contentBlocks ?? sub.content_blocks ?? []),
      references: transformReferences(sub.references ?? []),
      metadata: transformMetadata(sub.metadata),
    }));
  }

  // Transform contrast data if present
  if (response.contrast) {
    result.contrast = {
      headline: response.contrast.headline,
      contentBlocks: transformContentBlocks(response.contrast.contentBlocks ?? response.contrast.content_blocks ?? []),
      references: transformReferences(response.contrast.references ?? []),
      highlights: response.contrast.highlights ?? [],
      generatedAt: response.contrast.generatedAt ?? response.contrast.generated_at ?? '',
      headlinesEvaluated: response.contrast.headlinesEvaluated ?? response.contrast.headlines_evaluated,
    };
  }

  return result;
}
